import { cloneElement, isValidElement } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import type { VariantProps } from '@joyful/utils'
import {
  cn,
  createNonNullableContext,
  focusRingClassName,
  tv,
} from '@joyful/utils'

import { Slot } from '../Slot'

const statusPillBorderClassName = {
  brand: '[--status-pill-border:#e4cd82] dark:[--status-pill-border:#78350f]',
  failure: '[--status-pill-border:#f2b6b6] dark:[--status-pill-border:#7f1d1d]',
  info: '[--status-pill-border:#a8cdf8] dark:[--status-pill-border:#1e3a8a]',
  inverse: '[--status-pill-border:#cbd5e1] dark:[--status-pill-border:#475569]',
  neutral: '[--status-pill-border:#d1d5db] dark:[--status-pill-border:#475569]',
  resolved:
    '[--status-pill-border:#a5b4fc] dark:[--status-pill-border:#3730a3]',
  success: '[--status-pill-border:#8bd8bd] dark:[--status-pill-border:#064e3b]',
} as const

export const statusPillVariants = tv({
  base: 'relative inline-flex max-w-full items-center text-xs leading-4 font-medium whitespace-nowrap [[type=button]]:not-aria-disabled:cursor-pointer aria-disabled:cursor-not-allowed aria-disabled:opacity-50',

  defaultVariants: {
    join: 'pair',
    overflowBehavior: 'truncate',
    size: 'sm',
    variant: 'neutral',
  },

  slots: {
    lead: [
      focusRingClassName,
      'box-border inline-flex h-6 items-center gap-x-1 rounded-lg border border-[var(--status-pill-border)] pl-2.5 pr-2 font-medium whitespace-nowrap',
    ],
    leadContent: null,
    tail: [
      focusRingClassName,
      'box-border inline-flex h-6 min-w-0 items-center rounded-r-lg rounded-l-none border border-l-0 border-[var(--status-pill-border)] pl-2 pr-2.5 font-normal whitespace-nowrap',
    ],
    tailContent: null,
  },

  variants: {
    join: {
      group: {
        lead: 'rounded-none shadow-none not-first:-ml-px first:rounded-l-lg last:rounded-r-lg',
        tail: 'rounded-none shadow-none not-first:-ml-px not-first:ml-0 first:rounded-l-lg last:rounded-r-lg',
      },
      pair: {
        lead: '[[data-status-pill]:has([data-slot=status-pill-tail])_&]:rounded-r-none',
      },
    },
    overflowBehavior: {
      none: null,
      truncate: {
        base: 'min-w-0',
        lead: 'shrink-0',
        tail: 'min-w-6 shrink-[999] transition-[max-width,transform] duration-150 ease-out motion-reduce:transition-none',
        tailContent: 'min-w-0 truncate',
      },
    },
    size: {
      lg: {
        base: 'text-sm',
      },
      sm: null,
      xs: {
        lead: 'h-5 px-1.5',
        tail: 'h-5 px-1.5',
      },
    },
    variant: {
      brand: {
        lead: [
          statusPillBorderClassName.brand,
          'bg-amber-100 text-amber-800 shadow-[2px_0_3px_-2px_rgb(120_70_10_/_0.12)] dark:bg-[#713f12] dark:text-amber-100 dark:shadow-[2px_0_3px_-2px_rgb(0_0_0_/_0.45)]',
        ],
        tail: [
          statusPillBorderClassName.brand,
          'bg-[#fffbeb] text-yellow-700 dark:bg-[#291b0b] dark:text-amber-200',
        ],
      },
      failure: {
        lead: [
          statusPillBorderClassName.failure,
          'bg-red-100 text-red-800 shadow-[2px_0_3px_-2px_rgb(153_27_27_/_0.12)] dark:bg-[#7f1d1d] dark:text-red-100 dark:shadow-[2px_0_3px_-2px_rgb(0_0_0_/_0.45)]',
        ],
        tail: [
          statusPillBorderClassName.failure,
          'bg-red-50 text-red-700 dark:bg-[#2a1010] dark:text-red-200',
        ],
      },
      info: {
        lead: [
          statusPillBorderClassName.info,
          'bg-blue-100 text-blue-800 shadow-[2px_0_3px_-2px_rgb(30_64_175_/_0.12)] dark:bg-[#1e3a8a] dark:text-blue-100 dark:shadow-[2px_0_3px_-2px_rgb(0_0_0_/_0.45)]',
        ],
        tail: [
          statusPillBorderClassName.info,
          'bg-blue-50 text-blue-700 dark:bg-[#0f1d3d] dark:text-blue-200',
        ],
      },
      inverse: {
        lead: [
          statusPillBorderClassName.inverse,
          'bg-slate-100 text-slate-800 shadow-[2px_0_3px_-2px_rgb(30_41_59_/_0.12)] dark:bg-slate-700 dark:text-slate-100 dark:shadow-[2px_0_3px_-2px_rgb(0_0_0_/_0.45)]',
        ],
        tail: [
          statusPillBorderClassName.inverse,
          'bg-gray-100 text-gray-800 dark:bg-slate-900 dark:text-slate-200',
        ],
      },
      neutral: {
        lead: [
          statusPillBorderClassName.neutral,
          'bg-gray-100 text-gray-800 shadow-[2px_0_3px_-2px_rgb(31_41_55_/_0.12)] dark:bg-slate-800 dark:text-slate-100 dark:shadow-[2px_0_3px_-2px_rgb(0_0_0_/_0.45)]',
        ],
        tail: [
          statusPillBorderClassName.neutral,
          'bg-gray-50 text-gray-700 dark:bg-slate-950 dark:text-slate-200',
        ],
      },
      resolved: {
        lead: [
          statusPillBorderClassName.resolved,
          'bg-indigo-100 text-indigo-800 shadow-[2px_0_3px_-2px_rgb(67_56_202_/_0.12)] dark:bg-[#312e81] dark:text-indigo-100 dark:shadow-[2px_0_3px_-2px_rgb(0_0_0_/_0.45)]',
        ],
        tail: [
          statusPillBorderClassName.resolved,
          'bg-indigo-50 text-indigo-700 dark:bg-[#181b3d] dark:text-indigo-200',
        ],
      },
      success: {
        lead: [
          statusPillBorderClassName.success,
          'bg-emerald-100 text-emerald-800 shadow-[2px_0_3px_-2px_rgb(6_95_70_/_0.12)] dark:bg-[#064e3b] dark:text-emerald-100 dark:shadow-[2px_0_3px_-2px_rgb(0_0_0_/_0.45)]',
        ],
        tail: [
          statusPillBorderClassName.success,
          'bg-emerald-50 text-emerald-700 dark:bg-[#09251d] dark:text-emerald-200',
        ],
      },
    },
  },
})

type StatusPillLayoutProps = Pick<
  VariantProps<typeof statusPillVariants>,
  'join' | 'overflowBehavior' | 'size'
>

const StatusPillContext =
  createNonNullableContext<StatusPillLayoutProps>('StatusPillContext')

export type StatusPillProps = Omit<ComponentProps<'span'>, 'className'> &
  StatusPillLayoutProps

export function StatusPill({
  join,
  overflowBehavior,
  size,
  ...props
}: StatusPillProps) {
  const layout = { join, overflowBehavior, size }
  const classNames = statusPillVariants(layout)
  // SAFETY: Slot parents (e.g. PopoverTrigger asChild) inject className at runtime onto StatusPillProps which omits it.
  const { className: injectedClassName, ...domProps } =
    props as typeof props & {
      className?: string
    }

  return (
    <StatusPillContext.Context value={layout}>
      <span
        className={cn(classNames.base(), injectedClassName)}
        data-status-pill=""
        {...domProps}
      />
    </StatusPillContext.Context>
  )
}

export type StatusPillVariant = NonNullable<
  VariantProps<typeof statusPillVariants>['variant']
>

export interface StatusPillLeadProps
  extends
    Omit<ComponentProps<'span'>, 'className' | 'slot'>,
    Pick<VariantProps<typeof statusPillVariants>, 'variant'> {
  asChild?: boolean
  startAdornment?: ReactNode
}

export function StatusPillLead({
  asChild = false,
  children,
  startAdornment,
  variant,
  ...props
}: StatusPillLeadProps) {
  const { className, contentClassName } = useStatusPillSegmentClassNames({
    slot: 'lead',
    variant,
  })

  return (
    <StatusPillSegment
      asChild={asChild}
      className={className}
      contentClassName={contentClassName}
      startAdornment={startAdornment}
      {...props}
      slot="status-pill-lead"
    >
      {children}
    </StatusPillSegment>
  )
}

export type StatusPillTailProps = StatusPillLeadProps

export function StatusPillTail({
  asChild = false,
  children,
  startAdornment,
  variant,
  ...props
}: StatusPillTailProps) {
  const { className, contentClassName } = useStatusPillSegmentClassNames({
    slot: 'tail',
    variant,
  })

  return (
    <StatusPillSegment
      asChild={asChild}
      className={className}
      contentClassName={contentClassName}
      startAdornment={startAdornment}
      {...props}
      slot="status-pill-tail"
    >
      {children}
    </StatusPillSegment>
  )
}

function useStatusPillSegmentClassNames({
  slot,
  variant,
}: {
  slot: 'lead' | 'tail'
  variant: StatusPillLeadProps['variant']
}) {
  const layout = StatusPillContext.useRequiredContext()
  const classNames = statusPillVariants({ ...layout, variant })
  if (slot === 'tail') {
    return {
      className: classNames.tail(),
      contentClassName: classNames.tailContent(),
    }
  }

  return {
    className: classNames.lead(),
    contentClassName: classNames.leadContent(),
  }
}

function StatusPillSegment({
  asChild = false,
  children,
  className,
  contentClassName,
  slot,
  startAdornment,
  ...props
}: Omit<ComponentProps<'span'>, 'className'> & {
  asChild?: boolean
  className: string
  contentClassName: string
  slot: 'status-pill-lead' | 'status-pill-tail'
  startAdornment?: ReactNode
}) {
  // SAFETY: Slot parents (e.g. PopoverTrigger asChild) inject className at runtime onto StatusPill segment props which omit it.
  const { className: injectedClassName, ...domProps } =
    props as typeof props & {
      className?: string
    }
  const segmentClassName = cn(className, injectedClassName)

  if (asChild) {
    return (
      <Slot className={segmentClassName} data-slot={slot} {...domProps}>
        {statusPillAsChildContent({
          children,
          contentClassName,
          startAdornment,
        })}
      </Slot>
    )
  }

  return (
    <span className={segmentClassName} data-slot={slot} {...domProps}>
      {startAdornment}
      <span className={contentClassName}>{children}</span>
    </span>
  )
}

function statusPillAsChildContent({
  children,
  contentClassName,
  startAdornment,
}: {
  children: ReactNode
  contentClassName: string
  startAdornment?: ReactNode
}) {
  if (!isValidElement<{ children: ReactNode }>(children)) {
    return null
  }

  // oxlint-disable-next-line react/no-clone-element
  return cloneElement(
    children,
    undefined,
    startAdornment,
    <span className={contentClassName}>{children.props.children}</span>,
  )
}
